import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../../services/api.ts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../navbar/Navbar";
import PatientTable from "./PatientTable";
import PatientDataTable from "./PatientDataTable";
import HumanBodyDiagram from "./HumanBodyDiagram";
import BodyPartDataModal from "./BodyPartDataModal";
import PatientDataUpload from "./PatientDataUpload";
import PatientDatasets from "./PatientDatasets";
import "./Patients.css";
import "../studies/ViewerCreationLab.css";
import { getMediaUrl } from "../../services/media.ts";

const PREVIEWABLE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg']);
const DATA_TYPE_COLORS = { imaging: "primary", image: "success", document: "warning", table: "info", signal: "secondary", pathology: "danger" };
const DATA_TYPE_ICONS  = { imaging: "fa-hospital-o", image: "fa-image", document: "fa-file-text", table: "fa-table", signal: "fa-heartbeat", pathology: "fa-microscope" };

const getGenderLabel = (g) => ({ M: "Male", F: "Female", O: "Other", U: "Unknown" }[g] || "Unknown");

const AccordionSection = ({ title, icon, children, defaultOpen = false, badge }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="lab-accordion">
            <button
                className={`lab-accordion-header ${open ? "open" : ""}`}
                onClick={() => setOpen(o => !o)}
            >
                <i className={`fa ${icon} accordion-icon`}></i>
                <span>{title}</span>
                {badge != null && (
                    <span style={{
                        marginLeft: "6px",
                        background: "rgba(100,149,237,0.2)",
                        color: "#6495ED",
                        borderRadius: "10px",
                        padding: "1px 7px",
                        fontSize: "10px",
                        fontWeight: "700",
                    }}>
                        {badge}
                    </span>
                )}
                <i className="fa fa-chevron-down accordion-chevron"></i>
            </button>
            {open && <div className="lab-accordion-body" style={{ padding: "14px 20px 18px" }}>{children}</div>}
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.6px", textTransform: "uppercase", fontFamily: "Poppins, sans-serif", flexShrink: 0, marginRight: "12px" }}>{label}</span>
        <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.85)", fontFamily: "Poppins, sans-serif", textAlign: "right" }}>{value || "—"}</span>
    </div>
);

const DataStat = ({ icon, count, label, color }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "14px 8px", borderRadius: "10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
        <i className={`fa ${icon}`} style={{ color, fontSize: "22px", marginBottom: "6px" }} />
        <span style={{ fontSize: "18px", fontWeight: "700", color: "rgba(255,255,255,0.9)", fontFamily: "Poppins, sans-serif", lineHeight: 1 }}>{count}</span>
        <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", fontFamily: "Poppins, sans-serif", letterSpacing: "0.8px", textTransform: "uppercase", marginTop: "4px" }}>{label}</span>
    </div>
);

const PatientCreator = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Patient form state
    const [patientId, setPatientId] = useState("");
    const [patientName, setPatientName] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [gender, setGender] = useState("");
    const [notes, setNotes] = useState("");

    // UI state
    const [submitting, setSubmitting] = useState(false);
    const [createdPatient, setCreatedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientDataItems, setPatientDataItems] = useState([]);
    const [loadingPatients, setLoadingPatients] = useState(false);

    // Body diagram state
    const [zoom, setZoom] = useState(1.0);
    const [bodyPointPreview, setBodyPointPreview] = useState(null);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const previewAreaRef = useRef(null);
    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, panX: 0, panY: 0, moved: false });
    const [selectedBodyPart, setSelectedBodyPart] = useState(null);
    const [showBodyPartModal, setShowBodyPartModal] = useState(false);
    const [clickedPoints, setClickedPoints] = useState([]);

    // Annotation/Map upload modal state
    const [showAnnotationModal, setShowAnnotationModal] = useState(false);
    const [annotationUploadMode, setAnnotationUploadMode] = useState(null);
    const [annotationParentItem, setAnnotationParentItem] = useState(null);

    // Edit patient state
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState("");
    const [editDateOfBirth, setEditDateOfBirth] = useState("");
    const [editGender, setEditGender] = useState("");
    const [editNotes, setEditNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchPatients = useCallback(async () => {
        setLoadingPatients(true);
        try {
            const response = await API.get('/data_manager/patients/');
            setPatients(response.data);
        } catch (err) {
            console.error("Error fetching patients:", err);
            toast.error("Failed to fetch patients.");
        } finally {
            setLoadingPatients(false);
        }
    }, []);

    const fetchPatientData = useCallback(async (patientPk) => {
        try {
            const response = await API.get(`/data_manager/patients/${patientPk}/data/`);
            setPatientDataItems(response.data);
        } catch (err) {
            console.error("Error fetching patient data:", err);
            toast.error("Failed to fetch patient data.");
        }
    }, []);

    useEffect(() => { fetchPatients(); }, [fetchPatients]);

    // Reset pan/zoom when switching patients
    useEffect(() => { setPan({ x: 0, y: 0 }); }, [selectedPatient?.id]);

    // Global drag events for body diagram pan
    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current.dragging) return;
            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
            setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
        };
        const onUp = () => { dragRef.current.dragging = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);

    // Scroll-to-zoom on body diagram area
    useEffect(() => {
        const el = previewAreaRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoom(z => Math.min(Math.max(parseFloat((z + delta).toFixed(1)), 0.4), 3));
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, []);

    const handlePreviewMouseDown = (e) => {
        if (e.button !== 0) return;
        dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y, moved: false };
    };

    useEffect(() => {
        const points = patientDataItems
            .filter(item => item.metadata?.clickPosition)
            .map(item => item.metadata.clickPosition);
        setClickedPoints(points);
    }, [patientDataItems]);

    useEffect(() => {
        const selectedPatientId = location.state?.selectedPatientId;
        if (selectedPatientId && patients.length > 0) {
            const patient = patients.find(p => p.id === selectedPatientId);
            if (patient) {
                setSelectedPatient(patient);
                setCreatedPatient(patient);
                fetchPatientData(patient.id);
            }
        }
    }, [location.state?.selectedPatientId, patients, fetchPatientData]);

    const handleCreatePatient = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await API.post('/data_manager/patients/', {
                patient_id: patientId.trim() || null,
                name: patientName || null,
                date_of_birth: dateOfBirth || null,
                gender: gender || null,
                notes: notes || null
            }, { headers: { 'Content-Type': 'application/json' } });
            setCreatedPatient(response.data);
            setSelectedPatient(response.data);
            setPatientDataItems([]);
            toast.success(`Patient ${response.data.patient_id} created!`);
            fetchPatients();
            setPatientId(""); setPatientName(""); setDateOfBirth(""); setGender(""); setNotes("");
        } catch (err) {
            toast.error(err.response?.data?.patient_id ? "Patient ID already exists." : "Failed to create patient.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleQuickCreatePatient = async () => {
        setSubmitting(true);
        try {
            const response = await API.post('/data_manager/patients/', {}, { headers: { 'Content-Type': 'application/json' } });
            setCreatedPatient(response.data);
            setSelectedPatient(response.data);
            setPatientDataItems([]);
            toast.success(`Patient ${response.data.patient_id} created!`);
            fetchPatients();
        } catch (err) {
            toast.error("Failed to create patient.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleBodyPartClick = (clickPosition) => {
        if (!selectedPatient) { toast.warn("Please select a patient first."); return; }
        setClickedPoints(prev => [...prev, clickPosition]);
        setSelectedBodyPart(clickPosition);
        setShowBodyPartModal(true);
    };

    const handleBodyPartClickGuarded = useCallback((clickPosition) => {
        if (dragRef.current.moved) return;
        handleBodyPartClick(clickPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPatient]);

    const handleDotMoved = useCallback(async (oldPos, newPos) => {
        const eps = 0.5;
        const items = patientDataItems.filter(item => {
            const p = item.metadata?.clickPosition;
            return p && Math.abs(p.x - oldPos.x) < eps && Math.abs(p.y - oldPos.y) < eps;
        });
        if (items.length === 0) return;
        try {
            await Promise.all(items.map(item => {
                const fd = new FormData();
                fd.append('metadata', JSON.stringify({ ...item.metadata, clickPosition: newPos }));
                return API.patch(`/data_manager/patient-data/${item.id}/`, fd);
            }));
            if (selectedPatient) fetchPatientData(selectedPatient.id);
        } catch (err) {
            const msg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
            toast.error(`Move failed: ${msg}`);
        }
    }, [patientDataItems, selectedPatient, fetchPatientData]);

    const handleDataUploaded = async (newItems) => {
        toast.success(`${newItems.length} file(s) uploaded successfully!`);
        setShowBodyPartModal(false);
        if (selectedPatient) await fetchPatientData(selectedPatient.id);
    };

    const handleUploadAnnotation = (parentItem) => {
        setAnnotationParentItem(parentItem);
        setAnnotationUploadMode('annotation');
        setShowAnnotationModal(true);
    };

    const handleUploadMap = (parentItem) => {
        setAnnotationParentItem(parentItem);
        setAnnotationUploadMode('map');
        setShowAnnotationModal(true);
    };

    const handleAnnotationUploaded = async (newItems) => {
        const label = annotationUploadMode === 'map' ? 'map' : 'annotation';
        toast.success(`${newItems.length} ${label}(s) uploaded!`);
        setShowAnnotationModal(false);
        setAnnotationParentItem(null);
        setAnnotationUploadMode(null);
        if (selectedPatient) await fetchPatientData(selectedPatient.id);
    };

    const handleSelectPatient = (patient) => {
        setSelectedPatient(patient);
        setCreatedPatient(patient);
        fetchPatientData(patient.id);
    };

    const handleRemoveDataItem = async (itemId) => {
        try {
            await API.delete(`/data_manager/patient-data/${itemId}/`);
            setPatientDataItems(prev => prev.filter(item => item.id !== itemId));
            toast.success("Data item removed.");
        } catch (err) {
            toast.error("Failed to remove data item.");
        }
    };

    const getDataCountByType = (type) => patientDataItems.filter(item => item.data_type === type).length;

    const handleStartEdit = () => {
        if (selectedPatient) {
            setEditName(selectedPatient.name || "");
            setEditDateOfBirth(selectedPatient.date_of_birth || "");
            setEditGender(selectedPatient.gender || "");
            setEditNotes(selectedPatient.notes || "");
            setIsEditing(true);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const handleSavePatient = async () => {
        if (!selectedPatient) return;
        setSaving(true);
        try {
            const response = await API.patch(`/data_manager/patients/${selectedPatient.id}/`, {
                name: editName || null,
                date_of_birth: editDateOfBirth || null,
                gender: editGender || null,
                notes: editNotes || null
            });
            setSelectedPatient(response.data);
            setCreatedPatient(response.data);
            setIsEditing(false);
            toast.success("Patient updated!");
            fetchPatients();
        } catch (err) {
            toast.error("Failed to update patient.");
        } finally {
            setSaving(false);
        }
    };

    const labInputStyle = {
        width: "100%",
        background: "rgba(0,0,0,0.3)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "7px",
        padding: "9px 13px",
        color: "rgba(255,255,255,0.9)",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif",
        outline: "none",
        marginBottom: "12px",
        boxSizing: "border-box",
    };

    const labLabelStyle = {
        display: "block",
        fontSize: "11px",
        color: "rgba(255,255,255,0.45)",
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        fontFamily: "Poppins, sans-serif",
        marginBottom: "5px",
    };

    return (
        <div className="patients-page" style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
            <video autoPlay muted loop className="video-background">
                <source src="/assets/stars.mp4" type="video/mp4" />
            </video>
            <div className="video-overlay"></div>
            <Navbar />
            <ToastContainer autoClose={3000} />

            {/* Top bar */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 20px",
                background: "rgba(10,13,28,0.92)",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                flexShrink: 0,
                position: "relative",
                zIndex: 5,
            }}>
                <button
                    onClick={() => {
                        if (selectedPatient) {
                            setSelectedPatient(null);
                            setCreatedPatient(null);
                            setPatientDataItems([]);
                            setIsEditing(false);
                        } else {
                            navigate("/Datasets");
                        }
                    }}
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", padding: "6px 14px", color: "rgba(255,255,255,0.75)", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}
                >
                    <i className="fa fa-arrow-left me-2" />
                    {selectedPatient ? "Back to patient list" : "Back to Datasets"}
                </button>

                <div style={{ flex: 1, textAlign: "center" }}>
                    <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", fontFamily: "Poppins, sans-serif", letterSpacing: "1.5px", textTransform: "uppercase" }}>
                        Patient Data Manager
                        {selectedPatient && (
                            <span style={{ color: "#6495ED", marginLeft: "8px" }}>· {selectedPatient.patient_id}</span>
                        )}
                    </span>
                </div>

                {selectedPatient ? (
                    <button
                        onClick={handleQuickCreatePatient}
                        disabled={submitting}
                        style={{ background: "rgba(100,149,237,0.15)", border: "1px solid rgba(100,149,237,0.35)", borderRadius: "6px", padding: "6px 14px", color: "#6495ED", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}
                    >
                        <i className="fa fa-plus me-2" />
                        New Patient
                    </button>
                ) : (
                    <div style={{ width: "110px" }} />
                )}
            </div>

            {/* Main split */}
            <div className="lab-main" style={{ position: "relative", zIndex: 1 }}>

                {/* Left: Body diagram / placeholder */}
                <div
                    ref={previewAreaRef}
                    className="lab-preview-area"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(120,80,200,0.18)", position: "relative", overflow: "hidden", cursor: selectedPatient ? "grab" : "default" }}
                    onMouseDown={selectedPatient ? handlePreviewMouseDown : undefined}
                >
                    {selectedPatient ? (
                        <>
                            {/* Zoom + pan controls */}
                            <div style={{ position: "absolute", bottom: "16px", right: "16px", zIndex: 10, display: "flex", flexDirection: "column", gap: "4px" }}>
                                <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(+(z + 0.2).toFixed(1), 3)); }} title="Zoom in"
                                    style={{ width: "30px", height: "30px", background: "rgba(10,13,28,0.75)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "rgba(255,255,255,0.8)", fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" }}>+</button>
                                <button onClick={(e) => { e.stopPropagation(); setZoom(1.0); setPan({ x: 0, y: 0 }); }} title="Reset"
                                    style={{ width: "30px", height: "30px", background: "rgba(10,13,28,0.75)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "rgba(255,255,255,0.5)", fontSize: "9px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Poppins, sans-serif" }}>{Math.round(zoom * 100)}%</button>
                                <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(+(z - 0.2).toFixed(1), 0.4)); }} title="Zoom out"
                                    style={{ width: "30px", height: "30px", background: "rgba(10,13,28,0.75)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "rgba(255,255,255,0.8)", fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" }}>−</button>
                            </div>
                            <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center center", transition: dragRef.current.dragging ? "none" : "transform 0.15s ease" }}>
                                <HumanBodyDiagram
                                    onBodyPartClick={handleBodyPartClickGuarded}
                                    onDataPointClick={setBodyPointPreview}
                                    onDotMoved={handleDotMoved}
                                    dataItems={patientDataItems}
                                />
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.2)", fontFamily: "Poppins, sans-serif" }}>

                            <i className="fa fa-user-md" style={{ fontSize: "72px", marginBottom: "20px", display: "block" }} />
                            <p style={{ fontSize: "15px", margin: 0 }}>Select or create a patient</p>
                            <p style={{ fontSize: "12px", marginTop: "8px", color: "rgba(255,255,255,0.15)" }}>
                                Use the panel on the right
                            </p>
                        </div>
                    )}
                </div>

                {/* Right: Sidebar */}
                <div className="lab-sidebar" style={{ width: "50%", flexShrink: 0 }}>
                    {selectedPatient ? (
                        <>
                            {/* Patient Information */}
                            <AccordionSection title="PATIENT INFORMATION" icon="fa-user" defaultOpen={true}>
                                {isEditing ? (
                                    <div>
                                        <label style={labLabelStyle}>Name</label>
                                        <input style={labInputStyle} type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Patient name" />
                                        <label style={labLabelStyle}>Date of Birth</label>
                                        <input style={labInputStyle} type="date" value={editDateOfBirth} onChange={e => setEditDateOfBirth(e.target.value)} />
                                        <label style={labLabelStyle}>Gender</label>
                                        <select style={labInputStyle} value={editGender} onChange={e => setEditGender(e.target.value)}>
                                            <option value="">Select gender</option>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                            <option value="O">Other</option>
                                            <option value="U">Unknown</option>
                                        </select>
                                        <label style={labLabelStyle}>Notes</label>
                                        <textarea style={{ ...labInputStyle, resize: "vertical", minHeight: "60px" }} value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Notes..." />
                                        <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                                            <button onClick={handleCancelEdit} style={{ flex: 1, padding: "7px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "5px", color: "rgba(255,255,255,0.6)", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                                                Cancel
                                            </button>
                                            <button onClick={handleSavePatient} disabled={saving} style={{ flex: 1, padding: "7px", background: "rgba(100,149,237,0.2)", border: "1px solid rgba(100,149,237,0.4)", borderRadius: "5px", color: "#6495ED", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                                                {saving ? "Saving..." : "Save"}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <InfoRow label="Patient ID" value={selectedPatient.patient_id} />
                                        <InfoRow label="Name" value={selectedPatient.name} />
                                        <InfoRow label="Date of Birth" value={selectedPatient.date_of_birth} />
                                        <InfoRow label="Gender" value={getGenderLabel(selectedPatient.gender)} />
                                        {selectedPatient.notes && <InfoRow label="Notes" value={selectedPatient.notes} />}
                                        <button
                                            onClick={handleStartEdit}
                                            style={{ marginTop: "6px", width: "100%", padding: "7px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "5px", color: "rgba(255,255,255,0.55)", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}
                                        >
                                            <i className="fa fa-pencil me-2" />Edit
                                        </button>
                                    </div>
                                )}
                            </AccordionSection>

                            {/* Data Summary */}
                            <AccordionSection title="DATA SUMMARY" icon="fa-bar-chart" defaultOpen={true} badge={patientDataItems.length}>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                                    <DataStat icon="fa-hospital-o"   count={getDataCountByType('imaging')}   label="Imaging"   color="#6495ED" />
                                    <DataStat icon="fa-image"        count={getDataCountByType('image')}     label="Images"    color="#34d399" />
                                    <DataStat icon="fa-file-text"    count={getDataCountByType('document')}  label="Documents" color="#fbbf24" />
                                    <DataStat icon="fa-table"        count={getDataCountByType('table')}     label="Tables"    color="#38b6ff" />
                                    <DataStat icon="fa-heartbeat"    count={getDataCountByType('signal')}    label="Signals"   color="#a78bfa" />
                                    <DataStat icon="fa-video-camera" count={getDataCountByType('video')}     label="Videos"    color="#94a3b8" />
                                    <DataStat icon="fa-microscope"   count={getDataCountByType('pathology')} label="Pathology" color="#f87171" />
                                </div>
                            </AccordionSection>

                            {/* Datasets */}
                            <AccordionSection title="DATASETS" icon="fa-folder-open">
                                <PatientDatasets
                                    patient={selectedPatient}
                                    onDatasetsUpdated={fetchPatients}
                                />
                            </AccordionSection>

                            {/* Patient Data */}
                            <AccordionSection title="PATIENT DATA" icon="fa-list" badge={patientDataItems.length > 0 ? patientDataItems.length : undefined}>
                                {patientDataItems.length === 0 ? (
                                    <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", fontFamily: "Poppins, sans-serif", textAlign: "center", padding: "10px 0" }}>
                                        Click on body parts to add data
                                    </p>
                                ) : (
                                    /* Pull edge-to-edge so the table scrolls horizontally instead of squishing */
                                    <div style={{ margin: "0 -20px -18px", overflowX: "auto" }}>
                                        <PatientDataTable
                                            dataItems={patientDataItems}
                                            onRemoveItem={handleRemoveDataItem}
                                            onUploadAnnotation={handleUploadAnnotation}
                                            onUploadMap={handleUploadMap}
                                        />
                                    </div>
                                )}
                            </AccordionSection>
                        </>
                    ) : (
                        <>
                            {/* Quick create */}
                            <AccordionSection title="CREATE PATIENT" icon="fa-user-plus" defaultOpen={false}>
                                <button
                                    onClick={handleQuickCreatePatient}
                                    disabled={submitting}
                                    style={{ width: "100%", padding: "9px", background: "rgba(100,149,237,0.15)", border: "1px solid rgba(100,149,237,0.35)", borderRadius: "6px", color: "#6495ED", fontSize: "13px", fontFamily: "Poppins, sans-serif", cursor: "pointer", marginBottom: "14px" }}
                                >
                                    <i className="fa fa-bolt me-2" />{submitting ? "Creating..." : "Quick Create (auto-ID)"}
                                </button>
                                <form onSubmit={handleCreatePatient}>
                                    <label style={labLabelStyle}>Patient Name</label>
                                    <input style={labInputStyle} type="text" placeholder="Optional" value={patientName} onChange={e => setPatientName(e.target.value)} />
                                    <label style={labLabelStyle}>Patient ID</label>
                                    <input style={labInputStyle} type="text" placeholder="Auto-generated if empty" value={patientId} onChange={e => setPatientId(e.target.value)} />
                                    <label style={labLabelStyle}>Date of Birth</label>
                                    <input style={labInputStyle} type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} />
                                    <label style={labLabelStyle}>Gender</label>
                                    <select style={labInputStyle} value={gender} onChange={e => setGender(e.target.value)}>
                                        <option value="">Select gender</option>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                        <option value="O">Other</option>
                                        <option value="U">Unknown</option>
                                    </select>
                                    <label style={labLabelStyle}>Notes</label>
                                    <textarea style={{ ...labInputStyle, resize: "vertical", minHeight: "56px" }} placeholder="Optional notes..." value={notes} onChange={e => setNotes(e.target.value)} />
                                    <button type="submit" disabled={submitting} style={{ width: "100%", padding: "8px", background: "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.35)", borderRadius: "6px", color: "#34d399", fontSize: "12px", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                                        <i className="fa fa-user-plus me-2" />{submitting ? "Creating..." : "Create Patient"}
                                    </button>
                                </form>
                            </AccordionSection>

                            {/* Select existing */}
                            <AccordionSection title="SELECT EXISTING" icon="fa-users" defaultOpen={true} badge={patients.length}>
                                <div style={{ overflowX: "auto" }}>
                                    <PatientTable
                                        patients={patients}
                                        loading={loadingPatients}
                                        onSelectPatient={handleSelectPatient}
                                        selectedPatientId={selectedPatient?.id}
                                    />
                                </div>
                            </AccordionSection>
                        </>
                    )}
                </div>
            </div>

            {/* Body Point Preview Modal */}
            {bodyPointPreview && (
                <div
                    onClick={() => setBodyPointPreview(null)}
                    style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "0", maxWidth: "520px", width: "92vw", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 28px 72px rgba(0,0,0,0.85)", fontFamily: "Poppins, sans-serif" }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
                            <div>
                                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.35)", letterSpacing: "1.1px", textTransform: "uppercase", marginBottom: "2px" }}>Body Point Data</div>
                                <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.85)", fontWeight: "600" }}>{bodyPointPreview.length} file{bodyPointPreview.length !== 1 ? "s" : ""} at this location</div>
                            </div>
                            <button onClick={() => setBodyPointPreview(null)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: "18px", padding: "4px 8px" }}>
                                <i className="fa fa-times" />
                            </button>
                        </div>

                        {/* Items */}
                        <div style={{ overflowY: "auto", padding: "14px 20px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            {bodyPointPreview.map((item) => {
                                const ext = (item.original_file_type || "").toLowerCase();
                                const isImg = PREVIEWABLE_EXTS.has(ext);
                                const rawUrl = item.file || item.file_url || "";
                                const previewUrl = rawUrl ? getMediaUrl(rawUrl) : "";
                                return (
                                    <div key={item.id} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", overflow: "hidden" }}>
                                        {/* Item header */}
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px" }}>
                                            <span className={`badge bg-${DATA_TYPE_COLORS[item.data_type] || "secondary"}`} style={{ fontSize: "10px" }}>
                                                <i className={`fa ${DATA_TYPE_ICONS[item.data_type] || "fa-file"} me-1`} />
                                                {item.data_type}
                                            </span>
                                            <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", fontWeight: "500", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.filename}</span>
                                            {ext && <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", textTransform: "uppercase", flexShrink: 0 }}>{ext}</span>}
                                        </div>
                                        {/* Image preview */}
                                        {isImg && previewUrl && (
                                            <div style={{ background: "#080c1a", padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                                                <img
                                                    src={previewUrl}
                                                    alt={item.filename}
                                                    onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
                                                    style={{ maxWidth: "100%", maxHeight: "320px", borderRadius: "6px", display: "block", margin: "0 auto" }}
                                                />
                                                <div style={{ display: "none", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "12px", padding: "16px" }}>
                                                    <i className="fa fa-exclamation-circle me-2" />Could not load image
                                                </div>
                                            </div>
                                        )}
                                        {/* Non-image metadata */}
                                        {!isImg && item.description && (
                                            <div style={{ padding: "6px 14px 10px", borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: "12px", color: "rgba(255,255,255,0.45)" }}>
                                                {item.description}
                                            </div>
                                        )}
                                        {!isImg && !item.description && (
                                            <div style={{ padding: "6px 14px 10px", borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: "11px", color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>
                                                No preview available for this file type
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Body Part Data Modal */}
            <BodyPartDataModal
                show={showBodyPartModal}
                onHide={() => setShowBodyPartModal(false)}
                bodyPart={selectedBodyPart}
                patient={selectedPatient}
                onDataUploaded={handleDataUploaded}
            />

            {/* Annotation/Map Upload Modal */}
            {showAnnotationModal && annotationParentItem && (
                <>
                    <div className="modal-backdrop show" onClick={() => setShowAnnotationModal(false)}></div>
                    <div className="modal show body-part-modal" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" onClick={() => setShowAnnotationModal(false)}>
                        <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className={`fa ${annotationUploadMode === 'map' ? 'fa-map' : 'fa-tag'} me-2`}></i>
                                        Upload {annotationUploadMode === 'map' ? 'Map' : 'Annotation'}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setShowAnnotationModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-info mb-3">
                                        <i className="fa fa-info-circle me-2"></i>
                                        Upload {annotationUploadMode === 'map' ? 'a map' : 'an annotation'} for: <strong>{annotationParentItem.filename}</strong>
                                    </div>
                                    <PatientDataUpload
                                        patient={selectedPatient}
                                        dataType={annotationParentItem.data_type}
                                        imagingFormat={null}
                                        acceptedFormats=".nii,.nii.gz,.dcm,.geojson,.csv"
                                        noAcceptFilter={true}
                                        isAnnotation={annotationUploadMode === 'annotation'}
                                        isMap={annotationUploadMode === 'map'}
                                        parentDataId={annotationParentItem.id}
                                        onDataUploaded={handleAnnotationUploaded}
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAnnotationModal(false)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PatientCreator;
